import type { Planet, PlayerPremium } from '@kaeliux/shared';
import { parsePlayerPremium } from '@kaeliux/shared';
import { authedPost } from './http';
import type { PlanetActionOptions } from './planet.service';
import { normalizePlanet } from './planetNormalization';

/**
 * Premium purchases/entitlements (issue #99). Reads of the current premium
 * state come from `usePlayerStore.premium` (loaded via `science.service`'s
 * `getPlayerState`); these are the mutating calls, all server-authoritative
 * Cloud Functions. Each returns the updated `premium` so callers can push it
 * straight into `usePlayerStore` without a refetch.
 */

/** Instantly restore an ozone-locked planet to full Ozone. Returns the unlocked planet + new premium state. */
export async function purchaseEmergencyOzone(planetId: string): Promise<{ planet: Planet; premium: PlayerPremium }> {
    const data = await authedPost<Record<string, unknown>>('/purchaseEmergencyOzone', { planetId });
    return {
        planet: normalizePlanet(data, planetId),
        premium: parsePlayerPremium(data.premium),
    };
}

/** Buy a time-limited tier boost from the `PREMIUM_BOOST_PRODUCTS` catalog. */
export async function purchasePremiumBoost(productId: string): Promise<{ premium: PlayerPremium }> {
    const data = await authedPost<Record<string, unknown>>('/purchasePremiumBoost', { productId });
    return { premium: parsePlayerPremium(data.premium) };
}

/** Permanently unlock a premium planet image. */
export async function unlockPlanetImage(planetType: number): Promise<{ premium: PlayerPremium }> {
    const data = await authedPost<Record<string, unknown>>('/unlockPlanetImage', { planetType });
    return { premium: parsePlayerPremium(data.premium) };
}

/**
 * Claim a completed tutorial step's reward. Idempotent server-side. A
 * resources/ships reward is credited to `planetId` and comes back as the
 * updated `planet`; a premium reward only changes `premium`.
 */
export async function claimTutorialReward(
    stepIndex: number,
    planetId?: string,
): Promise<{ premium: PlayerPremium; granted: boolean; planet?: Planet }> {
    const data = await authedPost<Record<string, unknown>>('/claimTutorialReward', { stepIndex, planetId });
    return {
        premium: parsePlayerPremium(data.premium),
        granted: Boolean(data.granted),
        ...(data.planet && planetId ? { planet: normalizePlanet(data.planet as Record<string, unknown>, planetId) } : {}),
    };
}

/** Buy `quantity` Market trade tokens for `quantity * TRADE_TOKEN_PRICE` Dark Matter. */
export async function purchaseTradeTokens(quantity: number): Promise<{ premium: PlayerPremium }> {
    const data = await authedPost<Record<string, unknown>>('/purchaseTradeTokens', { quantity });
    return { premium: parsePlayerPremium(data.premium) };
}

/** Buy one Resource Pack for the given planet. Returns the topped-up planet + new Dark Matter balance. */
export async function purchaseResources(planetId: string, _options?: PlanetActionOptions): Promise<{ planet: Planet; premium: PlayerPremium }> {
    const data = await authedPost<Record<string, unknown>>('/purchaseResources', { planetId });
    return {
        planet: normalizePlanet(data, planetId),
        premium: parsePlayerPremium(data.premium),
    };
}
